import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { loadArtworkTexture } from '../artwork-texture.js';

// Pulsar - the circular visualizer of the music channels: a ring of spectrum
// bars around the cover, the cover itself blurred to the edges of the frame
// and shaking on every kick, particles shooting outward. EDM, trap, bass
// music, house, electronic pop.
//
// THE COVER IS THE SUBJECT, and it is already there: the DOM overlay at the
// centre of the frame. Everything here is laid out around it, in SCREEN
// space, from the safe zone's own measurement of where the overlay is. No
// camera is involved anywhere in this file - every vertex is emitted in
// clip space - so the scene manager's camera sway cannot move the ring off
// the cover.
//
// THE DATA. There are three envelopes (director.bands), not a spectrum, and
// an honest 96-bar ring has to be reconstructed from them: three wide
// gaussians along the ring, a +/-12% slow noise per bar so the ring never
// moves in three blocks, the vu-meter ballistics (instant attack, linear
// fall, a peak that hangs 300 ms) and a bass spike on every beat. Without a
// radiopi timeline the bands are synthetic (beat-shaped), as everywhere.

const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'pulsar',
  family: 'graphic',
  energyLevel: 0.84,
  bpmMin: 90,
  bpmMax: 180,
  movement: 0.75,
  danceability: 0.86,
  tonality: 0.55,
  prefers: { lowEnd: [0.50, 1.0] },
  enabled: true
};

const DEFAULT_TIER = 2;
const TIER = {
  2: { bins: 48, particles: 500, stars: 220 },
  1: { bins: 48, particles: 300, stars: 0 },
  0: { bins: 32, particles: 150, stars: 0 }
};

// --- the ring --------------------------------------------------------------
const RING_RADIUS_K = 1.18;    // x the cover's half-diagonal
// Below this cover half-side (height units) the cover is mini or gone and
// the ring is drawn around a default one at the centre of the frame.
const MIN_COVER_SIDE = 0.14;
const DEFAULT_COVER_SIDE = 0.30;
const BAR_WIDTH_K = 0.58;      // of the angular spacing (the brief's 0.70 fused into a toothed ring under the bloom)
const BAR_MAX_LEN = 0.22;      // of the frame height (height units: 0.44)
const PEAK_CAP_LEN = 0.012;    // height units
const DOUBLE_INNER_K = 0.86;   // DOUBLE: the inner ring sits closer to the cover
const DOUBLE_OUTER_K = 1.16;   // and the outer one just past the usual radius
const DOUBLE_LEN_K = 0.55;     // shorter bars, two rings have to fit the frame
const CURVE_SUBDIV = 4;        // Catmull-Rom samples per bar
const CURVE_PX = 2.2;          // the wave's core width in px
const WAVE_PX = 6.0;           // WAVE mode: thicker
const PULSE_SCALE = 1.04, PULSE_S = 0.15;
const NOTCH_S = 0.35;          // the bar's rotation tick settles in this time

// --- the data --------------------------------------------------------------
// Band centres along the ring (0 = bass, 1 = treble) and their width. The
// gaussians overlap widely on purpose: a bin between two bands takes from
// both, which is what keeps the ring continuous.
const BAND_CENTRES = [0.10, 0.48, 0.86];
const BAND_SIGMA = 0.24;
const TILT = 0.30;             // the natural spectral tilt: bass taller
const DECORR = 0.12;           // +/- per bar
const FALL_PER_S = 2.4;        // linear fall, full scale per second
const PEAK_HOLD_S = 0.30;
const PEAK_FALL_PER_S = 1.3;
const KICK_SPIKE = 0.45;
const KICK_BINS = 0.28;        // of the ring's bins, from the bass end

// --- the background --------------------------------------------------------
const PYRAMID = [512, 256, 128, 64];   // four halvings, then the blur at 64
const BG_DARKEN = 0.35, BG_BRIGHT = 0.50;
const BG_SATURATION = 1.2;
const BG_MEAN = 0.09;          // linear mean the blurred cover is brought to, before the 0.35 darkening
const SHAKE_PX = 3, SHAKE_ZOOM = 1.02, SHAKE_S = 0.12;
const DROP_SHAKE_K = 3.0;
const FLASH_FRAMES = 2;

// --- the particles ---------------------------------------------------------
const BEAT_BURST = [24, 48];   // weak, strong beat
const DROP_BURST = 300;
const PARTICLE_LIFE = [0.5, 1.1];
const PARTICLE_SPEED = [0.35, 0.9];   // height units per second, x bands.low
const PARTICLE_ACCEL = 1.6;
const TRAIL_S = 0.045;
const STAR_SPEED = 0.05;

const MODE_BARS = 0, MODE_DOTS = 1, MODE_WAVE = 2, MODE_DOUBLE = 3;
const MODE_NAMES = { bars: MODE_BARS, dots: MODE_DOTS, wave: MODE_WAVE, double: MODE_DOUBLE };
const MODE_COUNT = 4;
const DROP_RING_BEATS = 2;

const SYNTH_BASE = 0.10, SYNTH_DECAY = 1.9;
const SYNTH_PERIOD_LOW = 4.7, SYNTH_PERIOD_MID = 3.1, SYNTH_PERIOD_HIGH = 6.3;
const SYNTH_BPM = 128;
const FALLBACK_COLORS = [0x40c8ff, 0xff40a0, 0xffd040];

// =============================================================================
// SHADERS - everything in clip space. Height units: y in [-1, 1], x scaled
// by the aspect, the same frame safeZone.glsl.js measures the overlay in.
// =============================================================================

const BAR_VERTEX = `
  precision mediump float;
  attribute float aAngle;
  attribute float aRadius;
  attribute float aLen;
  attribute float aWidth;
  attribute float aT;        // position along the ring 0..1, for the gradient
  attribute float aCap;      // 1: this instance is the peak cap
  uniform float uAspect;
  uniform vec2 uCentre;
  uniform float uScale;
  uniform vec3 uColA, uColB, uColC;
  uniform float uGradShift;
  varying vec2 vLocal;
  varying float vRatio;
  varying vec3 vColor;
  varying float vCap;
  void main() {
    vec2 dir = vec2(cos(aAngle), sin(aAngle));
    vec2 perp = vec2(-dir.y, dir.x);
    float len = max(aLen, aWidth * 0.5);
    float along = aRadius * uScale + position.y * len;
    float across = position.x * aWidth;
    vec2 hu = uCentre + dir * along + perp * across;
    gl_Position = vec4(hu.x / uAspect, hu.y, 0.0, 1.0);
    vLocal = vec2(position.x * 2.0, position.y);
    vRatio = len / max(aWidth, 1e-4);
    float t = fract(aT + uGradShift) * 3.0;
    vColor = t < 1.0 ? mix(uColA, uColB, t) : (t < 2.0 ? mix(uColB, uColC, t - 1.0) : mix(uColC, uColA, t - 2.0));
    vCap = aCap;
  }
`;

// A rounded radial rectangle: white core, coloured halo. DOTS segments it
// like a vu-meter.
const BAR_FRAGMENT = `
  precision mediump float;
  uniform float uDots;
  uniform float uGain;
  varying vec2 vLocal;
  varying float vRatio;
  varying vec3 vColor;
  varying float vCap;
  void main() {
    // distance to the rounded box, in across-half-widths
    vec2 p = vec2(vLocal.x, (vLocal.y - 0.5) * 2.0 * vRatio);
    vec2 b = vec2(1.0, vRatio) - vec2(1.0);
    vec2 q = abs(p) - b;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - 1.0;
    float shape = 1.0 - smoothstep(-0.25, 0.05, d);
    float core = 1.0 - smoothstep(-0.9, -0.3, d);
    float seg = 1.0;
    if (uDots > 0.5 && vCap < 0.5) {
      float s = fract(vLocal.y * vRatio * 0.55);
      seg = smoothstep(0.08, 0.22, s) * (1.0 - smoothstep(0.78, 0.92, s));
    }
    vec3 col = (vColor * 0.55 + vec3(1.0) * core * 0.45) * shape * seg * uGain;
    if (vCap > 0.5) col = (vColor * 0.4 + vec3(0.6)) * shape * uGain;
    gl_FragColor = vec4(col, shape * seg);
  }
`;

// The smooth contour through the bar tops: a ribbon in clip space, its
// vertices written by the CPU. aSide is -1..1 across the ribbon.
const RIBBON_VERTEX = `
  precision mediump float;
  attribute float aSide;
  attribute float aT;
  uniform float uAspect;
  uniform vec3 uColA, uColB, uColC;
  uniform float uGradShift;
  varying float vSide;
  varying vec3 vColor;
  void main() {
    gl_Position = vec4(position.x / uAspect, position.y, 0.0, 1.0);
    vSide = aSide;
    float t = fract(aT + uGradShift) * 3.0;
    vColor = t < 1.0 ? mix(uColA, uColB, t) : (t < 2.0 ? mix(uColB, uColC, t - 1.0) : mix(uColC, uColA, t - 2.0));
  }
`;
const RIBBON_FRAGMENT = `
  precision mediump float;
  uniform float uGain;
  uniform float uFill;     // WAVE: the translucent fill under the curve
  varying float vSide;
  varying vec3 vColor;
  void main() {
    float u = abs(vSide);
    float core = 1.0 - smoothstep(0.0, 0.45, u);
    float halo = (1.0 - u) * (1.0 - u);
    vec3 col = (vColor * halo * 0.6 + vec3(1.0) * core * 0.7) * uGain;
    if (uFill > 0.5) col = vColor * 0.10 * (1.0 - u * 0.5) * uGain;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// Particles: short trails as line segments, additive, white tinted.
const TRAIL_VERTEX = `
  precision mediump float;
  attribute float aAlpha;
  uniform float uAspect;
  varying float vAlpha;
  void main() {
    gl_Position = vec4(position.x / uAspect, position.y, 0.0, 1.0);
    vAlpha = aAlpha;
  }
`;
const TRAIL_FRAGMENT = `
  precision mediump float;
  uniform vec3 uTint;
  uniform float uGain;
  varying float vAlpha;
  void main() {
    vec3 col = mix(vec3(1.0), uTint, 0.45) * vAlpha * uGain;
    gl_FragColor = vec4(col, 1.0);
  }
`;

const STAR_VERTEX = `
  precision mediump float;
  attribute float aBright;
  uniform float uAspect;
  uniform float uPointPx;
  varying float vBright;
  void main() {
    gl_Position = vec4(position.x / uAspect, position.y, 0.0, 1.0);
    gl_PointSize = uPointPx;
    vBright = aBright;
  }
`;
const STAR_FRAGMENT = `
  precision mediump float;
  varying float vBright;
  void main() {
    vec2 d = gl_PointCoord - 0.5;
    float r = length(d) * 2.0;
    if (r > 1.0) discard;
    float e = (1.0 - r) * (1.0 - r);
    gl_FragColor = vec4(vec3(e * vBright * 0.55), 1.0);
  }
`;

// The background: the blurred cover, cover-fitted, darkened, saturated,
// vignetted, shaken.
const BG_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const BG_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uBg;
  uniform float uHas;
  uniform vec2 uCover;        // cover-fit uv scale
  uniform vec2 uShake;        // uv offset
  uniform float uZoom;
  uniform float uDarken;
  uniform float uNorm;        // brings the blurred cover to a fixed mean luminance
  uniform float uFlash;
  uniform vec3 uFallback;
  varying vec2 vUv;
  void main() {
    vec2 uv = (vUv - 0.5) * uCover / uZoom + 0.5 + uShake;
    vec3 c = uHas > 0.5 ? texture2D(uBg, uv).rgb : uFallback;
    float l = dot(c, vec3(0.2126, 0.7152, 0.0722));
    c = mix(vec3(l), c, ${BG_SATURATION.toFixed(2)});
    // every cover lands at the same mean luminance (see _runBlur): the
    // ring is additive and would vanish on a near-white ground
    c *= uNorm;
    vec2 v = vUv - 0.5;
    float vig = 1.0 - smoothstep(0.12, 0.70, dot(v, v) * 2.2);
    c *= uDarken * vig;
    c += vec3(uFlash);
    gl_FragColor = vec4(c, 1.0);
  }
`;

// The blur chain: a plain copy (the GPU's bilinear does the halving) and a
// 9-tap gaussian, separable.
const PASS_VERTEX = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;
const COPY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  varying vec2 vUv;
  void main() { gl_FragColor = texture2D(uTex, vUv); }
`;
const GAUSS_FRAGMENT = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform vec2 uStep;
  varying vec2 vUv;
  void main() {
    vec4 c = texture2D(uTex, vUv) * 0.2270;
    c += (texture2D(uTex, vUv + uStep) + texture2D(uTex, vUv - uStep)) * 0.1945;
    c += (texture2D(uTex, vUv + uStep * 2.0) + texture2D(uTex, vUv - uStep * 2.0)) * 0.1216;
    c += (texture2D(uTex, vUv + uStep * 3.0) + texture2D(uTex, vUv - uStep * 3.0)) * 0.0540;
    c += (texture2D(uTex, vUv + uStep * 4.0) + texture2D(uTex, vUv - uStep * 4.0)) * 0.0162;
    gl_FragColor = c;
  }
`;

function urlParam(name) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(name);
}
function clamp01(v) { return v < 0 ? 0 : (v > 1 ? 1 : v); }
function lerp(a, b, t) { return a + (b - a) * t; }
function rand(a, b) { return a + Math.random() * (b - a); }

// Closed Catmull-Rom through pts (array of [x, y]), n samples per segment.
function catmullRom(pts, n, out) {
  const N = pts.length;
  let o = 0;
  for (let i = 0; i < N; i++) {
    const p0 = pts[(i - 1 + N) % N], p1 = pts[i], p2 = pts[(i + 1) % N], p3 = pts[(i + 2) % N];
    for (let s = 0; s < n; s++) {
      const t = s / n, t2 = t * t, t3 = t2 * t;
      out[o++] = 0.5 * ((2 * p1[0]) + (-p0[0] + p2[0]) * t + (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * t2 + (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * t3);
      out[o++] = 0.5 * ((2 * p1[1]) + (-p0[1] + p2[1]) * t + (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * t2 + (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * t3);
    }
  }
  return o / 2;
}

export class PulsarVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    this._layout = TIER[this._tier];
    // ?pulsarmode=bars|dots|wave|double pins a mode.
    const m = urlParam('pulsarmode');
    this._forcedMode = m != null && MODE_NAMES[m] !== undefined ? MODE_NAMES[m] : null;
    // ?pulsarraw=1 draws the reconstructed bins as a bar graph along the
    // bottom edge, bass left.
    this._rawView = urlParam('pulsarraw') === '1';
    // ?pulsarprobe=1 publishes the live state on window.__pulsar.
    this._probe = urlParam('pulsarprobe') === '1';
    this._postProcessing = { bloomStrength: 0.35, vignette: 0.2, aberration: 0.0015 };
  }

  get postProcessing() { return this._postProcessing; }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const L = this._layout;
    this._bins = L.bins;
    this._barCount = L.bins * 2;

    // --- the data
    const B = this._bins;
    this._target = new Float32Array(B);
    this._value = new Float32Array(B);
    this._peak = new Float32Array(B);
    this._peakHold = new Float32Array(B);
    this._noisePhase = new Float32Array(B);
    this._noiseRate = new Float32Array(B);
    for (let k = 0; k < B; k++) {
      this._noisePhase[k] = Math.random() * Math.PI * 2;
      // rates spread by construction (five distinct values, alternating
      // sign): two neighbours can never drift in step
      this._noiseRate[k] = (0.4 + 0.18 * (k % 5) + rand(0, 0.06)) * (k % 2 ? 1 : -1);
    }
    this._kick = 0;

    // --- shared uniforms
    this._shared = {
      uAspect: { value: 16 / 9 },
      uCentre: { value: new THREE.Vector2(0, 0) },
      uScale: { value: 1 },
      uColA: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uColB: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uColC: { value: new THREE.Color(FALLBACK_COLORS[2]) },
      uGradShift: { value: 0 },
      uGain: { value: 1 },
      uTint: { value: new THREE.Color(FALLBACK_COLORS[0]) }
    };

    this._buildBackground();
    this._buildBars();
    this._buildRibbons();
    this._buildParticles();
    if (L.stars) this._buildStars();

    // --- rhythm state
    this._time = 0;
    this._step = 0;
    this._sinceStep = 0;
    this._beatPeriod = 60 / SYNTH_BPM;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrasePhase = null;
    this._lastMode = null;
    this._mode = this._forcedMode != null ? this._forcedMode : MODE_BARS;
    this._shakeT = 99;
    this._shakeDir = new THREE.Vector2(1, 0);
    this._shakeK = 1;
    this._pulseT = 99;
    this._notchT = 99;
    this._notchDir = 1;
    this._gradShift = 0;
    this._flashFrames = 0;
    this._dropT = -1;
    this._bright = BG_DARKEN;
    this._bands = { low: 0.5, mid: 0.5, high: 0.5 };
    this._synth = { low: SYNTH_BASE, mid: SYNTH_BASE, high: SYNTH_BASE };
    this._synthPhase = { low: null, mid: null, high: null };
    this._lastPaletteRef = undefined;
    this._artUrl = undefined;
    this._artTexture = null;
    this._blurDirty = false;
    this._aspect = 0;
    this._centre = new THREE.Vector2(0, 0);
    this._radius = 0.5;
    this._scratch = new THREE.Color();
    this._hsl = { h: 0, s: 0, l: 0 };
    this._applyPalette(null);
    this._reframe();
    this._writeBars();
  }

  // --- geometry --------------------------------------------------------------

  _buildBackground() {
    this._bgUniforms = {
      uBg: { value: null },
      uHas: { value: 0 },
      uCover: { value: new THREE.Vector2(1, 1) },
      uShake: { value: new THREE.Vector2(0, 0) },
      uZoom: { value: 1 },
      uDarken: { value: BG_DARKEN },
      uNorm: { value: 1 },
      uFlash: { value: 0 },
      uFallback: { value: new THREE.Color(0x101828) }
    };
    this._bgMat = new THREE.ShaderMaterial({ uniforms: this._bgUniforms, vertexShader: BG_VERTEX, fragmentShader: BG_FRAGMENT, depthTest: false, depthWrite: false });
    this._bgGeo = new THREE.PlaneGeometry(2, 2);
    this._bg = new THREE.Mesh(this._bgGeo, this._bgMat);
    this._bg.frustumCulled = false;
    this._bg.renderOrder = -10;
    this._scene.add(this._bg);

    // the blur chain, run inside the background's onBeforeRender once per
    // new cover - never per frame
    this._passScene = new THREE.Scene();
    this._passCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this._copyMat = new THREE.ShaderMaterial({ uniforms: { uTex: { value: null } }, vertexShader: PASS_VERTEX, fragmentShader: COPY_FRAGMENT, depthTest: false, depthWrite: false });
    this._gaussMat = new THREE.ShaderMaterial({ uniforms: { uTex: { value: null }, uStep: { value: new THREE.Vector2(0, 0) } }, vertexShader: PASS_VERTEX, fragmentShader: GAUSS_FRAGMENT, depthTest: false, depthWrite: false });
    this._passQuad = new THREE.Mesh(this._bgGeo, this._copyMat);
    this._passQuad.frustumCulled = false;
    this._passScene.add(this._passQuad);
    this._pyramid = PYRAMID.map((s) => new THREE.WebGLRenderTarget(s, s, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false }));
    const s = PYRAMID[PYRAMID.length - 1];
    this._blurRT = new THREE.WebGLRenderTarget(s, s, { minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, stencilBuffer: false });
    const self = this;
    this._bg.onBeforeRender = function (renderer) {
      if (self._blurDirty && self._artTexture) self._runBlur(renderer);
    };
  }

  _runBlur(renderer) {
    this._blurDirty = false;
    const prev = renderer.getRenderTarget();
    let src = this._artTexture;
    for (let i = 0; i < this._pyramid.length; i++) {
      this._copyMat.uniforms.uTex.value = src;
      this._passQuad.material = this._copyMat;
      renderer.setRenderTarget(this._pyramid[i]);
      renderer.render(this._passScene, this._passCam);
      src = this._pyramid[i].texture;
    }
    const last = this._pyramid[this._pyramid.length - 1];
    const s = PYRAMID[PYRAMID.length - 1];
    this._passQuad.material = this._gaussMat;
    this._gaussMat.uniforms.uTex.value = last.texture;
    this._gaussMat.uniforms.uStep.value.set(1 / s, 0);
    renderer.setRenderTarget(this._blurRT);
    renderer.render(this._passScene, this._passCam);
    this._gaussMat.uniforms.uTex.value = this._blurRT.texture;
    this._gaussMat.uniforms.uStep.value.set(0, 1 / s);
    renderer.setRenderTarget(last);
    renderer.render(this._passScene, this._passCam);
    // the mean luminance of the blurred cover, read back once: the
    // background is normalised to BG_MEAN so a white cover and a black one
    // sit the ring on the same ground
    const px = this._readback || (this._readback = new Uint8Array(s * s * 4));
    renderer.readRenderTargetPixels(last, 0, 0, s, s, px);
    let sum = 0;
    for (let i = 0; i < s * s; i++) sum += 0.2126 * px[i * 4] + 0.7152 * px[i * 4 + 1] + 0.0722 * px[i * 4 + 2];
    const mean = sum / (s * s * 255);
    this._bgUniforms.uNorm.value = Math.min(4, Math.max(0.25, BG_MEAN / Math.max(mean, 0.01)));
    renderer.setRenderTarget(prev);
    this._bgUniforms.uBg.value = last.texture;
    this._bgUniforms.uHas.value = 1;
  }

  _buildBars() {
    const n = this._barCount * 2;      // bars, then their peak caps
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array([
      -0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0
    ]), 3));
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    const dyn = (size) => { const a = new THREE.InstancedBufferAttribute(new Float32Array(n * size), size); a.setUsage(THREE.DynamicDrawUsage); return a; };
    this._aAngle = dyn(1); this._aRadius = dyn(1); this._aLen = dyn(1); this._aWidth = dyn(1); this._aT = dyn(1);
    const cap = new Float32Array(n);
    for (let i = this._barCount; i < n; i++) cap[i] = 1;
    geometry.setAttribute('aAngle', this._aAngle);
    geometry.setAttribute('aRadius', this._aRadius);
    geometry.setAttribute('aLen', this._aLen);
    geometry.setAttribute('aWidth', this._aWidth);
    geometry.setAttribute('aT', this._aT);
    geometry.setAttribute('aCap', new THREE.InstancedBufferAttribute(cap, 1));
    geometry.instanceCount = n;
    this._barUniforms = Object.assign({ uDots: { value: 0 } }, this._shared);
    // DoubleSide, and it is not optional: the quad is re-based onto
    // (perp, dir), a basis of determinant -1, so every bar is a back face
    // and FrontSide culled all 96 of them (same trap as beamwall's impacts).
    this._barMat = new THREE.ShaderMaterial({ uniforms: this._barUniforms, vertexShader: BAR_VERTEX, fragmentShader: BAR_FRAGMENT, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
    this._barGeo = geometry;
    this._bars = new THREE.Mesh(geometry, this._barMat);
    this._bars.frustumCulled = false;
    this._bars.renderOrder = 2;
    this._scene.add(this._bars);

  }

  // Two ribbons: the bright contour through the bar tops, and WAVE's fill
  // from the ring radius up to the contour.
  _buildRibbons() {
    const samples = this._barCount * CURVE_SUBDIV;
    this._curveSamples = samples;
    this._curvePts = new Float32Array(samples * 2);
    this._curveIn = [];
    for (let i = 0; i < this._barCount; i++) this._curveIn.push([0, 0]);
    const make = (fill) => {
      const g = new THREE.BufferGeometry();
      const pos = new THREE.BufferAttribute(new Float32Array((samples + 1) * 2 * 3), 3);
      pos.setUsage(THREE.DynamicDrawUsage);
      const side = new Float32Array((samples + 1) * 2);
      const t = new THREE.BufferAttribute(new Float32Array((samples + 1) * 2), 1);
      t.setUsage(THREE.DynamicDrawUsage);
      for (let i = 0; i <= samples; i++) { side[i * 2] = -1; side[i * 2 + 1] = 1; }
      g.setAttribute('position', pos);
      g.setAttribute('aSide', new THREE.BufferAttribute(side, 1));
      g.setAttribute('aT', t);
      const idx = [];
      for (let i = 0; i < samples; i++) { const a = i * 2; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      g.setIndex(idx);
      const uniforms = Object.assign({ uFill: { value: fill ? 1 : 0 } }, this._shared);
      const mat = new THREE.ShaderMaterial({ uniforms: uniforms, vertexShader: RIBBON_VERTEX, fragmentShader: RIBBON_FRAGMENT, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false, side: THREE.DoubleSide });
      const mesh = new THREE.Mesh(g, mat);
      mesh.frustumCulled = false;
      mesh.renderOrder = fill ? 0 : 1;
      this._scene.add(mesh);
      return { geo: g, mat: mat, mesh: mesh, pos: pos, t: t };
    };
    this._curve = make(false);
    this._fill = make(true);
    this._fill.mesh.visible = false;
  }

  _buildParticles() {
    const n = this._layout.particles;
    this._pCount = n;
    this._pX = new Float32Array(n); this._pY = new Float32Array(n);
    this._pVX = new Float32Array(n); this._pVY = new Float32Array(n);
    this._pLife = new Float32Array(n); this._pMax = new Float32Array(n);
    this._pNext = 0;
    const g = new THREE.BufferGeometry();
    this._pPos = new THREE.BufferAttribute(new Float32Array(n * 2 * 3), 3);
    this._pPos.setUsage(THREE.DynamicDrawUsage);
    this._pAlpha = new THREE.BufferAttribute(new Float32Array(n * 2), 1);
    this._pAlpha.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this._pPos);
    g.setAttribute('aAlpha', this._pAlpha);
    this._trailMat = new THREE.ShaderMaterial({ uniforms: this._shared, vertexShader: TRAIL_VERTEX, fragmentShader: TRAIL_FRAGMENT, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    this._trailGeo = g;
    this._trails = new THREE.LineSegments(g, this._trailMat);
    this._trails.frustumCulled = false;
    this._trails.renderOrder = 1;
    this._scene.add(this._trails);
  }

  _buildStars() {
    const n = this._layout.stars;
    this._sCount = n;
    this._sR = new Float32Array(n); this._sA = new Float32Array(n);
    const bright = new Float32Array(n);
    for (let i = 0; i < n; i++) { this._sR[i] = Math.sqrt(Math.random()) * 1.6 + 0.1; this._sA[i] = Math.random() * Math.PI * 2; bright[i] = rand(0.3, 1); }
    const g = new THREE.BufferGeometry();
    this._sPos = new THREE.BufferAttribute(new Float32Array(n * 3), 3);
    this._sPos.setUsage(THREE.DynamicDrawUsage);
    g.setAttribute('position', this._sPos);
    g.setAttribute('aBright', new THREE.BufferAttribute(bright, 1));
    this._starMat = new THREE.ShaderMaterial({ uniforms: Object.assign({ uPointPx: { value: 2.5 } }, this._shared), vertexShader: STAR_VERTEX, fragmentShader: STAR_FRAGMENT, transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false });
    this._starGeo = g;
    this._stars = new THREE.Points(g, this._starMat);
    this._stars.frustumCulled = false;
    this._stars.renderOrder = -5;
    this._scene.add(this._stars);
  }

  // --- the frame: the ring sits on the DOM cover ------------------------------------
  _reframe() {
    const aspect = safeZoneUniforms.uAspect.value || 16 / 9;
    this._aspect = aspect;
    this._shared.uAspect.value = aspect;
    // The overlay box spans cover plus title and artist; the cover is the
    // square at its top (see focus.js's _fitCover).
    const half = safeZoneUniforms.uSafeHalfSize.value;
    const centre = safeZoneUniforms.uSafeCenter.value;
    let side = Math.min(half.x, half.y);
    // No cover to sit on - hidden (the safe zone is then parked off screen
    // with a zero size) or shrunk to its mini size: the ring stays, at the
    // centre of the frame, around an imaginary cover of the usual size.
    const absent = side < MIN_COVER_SIDE || Math.abs(centre.x) > 1.5 || Math.abs(centre.y) > 1.5;
    // R19: present, the ring is drawn around the cover AS IT WOULD BE at the
    // stage centre - a moved cover does not take the ring with it.
    if (absent) { side = DEFAULT_COVER_SIDE; this._centre.set(0, 0); }
    else {
      const sc = safeZoneUniforms.uStageCenter.value, sh = safeZoneUniforms.uStageHalfSize.value;
      side = Math.min(sh.x, sh.y);
      this._centre.set(sc.x, sc.y + sh.y - side);
    }
    this._coverAbsent = absent;
    this._radius = RING_RADIUS_K * side * Math.SQRT2;
    this._shared.uCentre.value.copy(this._centre);
    // the blurred cover fits the frame
    const tex = this._artTexture;
    const texAspect = tex && tex.image && tex.image.width ? tex.image.width / tex.image.height : 1;
    const ratio = aspect / texAspect;
    this._bgUniforms.uCover.value.set(ratio > 1 ? 1 : ratio, ratio > 1 ? 1 / ratio : 1);
  }

  // --- the palette: a gradient of three vivid colours along the ring ------------------
  _applyPalette(palette) {
    const vivid = palette && palette.vividColors && palette.vividColors.length ? palette.vividColors : FALLBACK_COLORS;
    const pick = (i) => vivid[i % vivid.length];
    this._shared.uColA.value.set(pick(0));
    this._shared.uColB.value.set(pick(1));
    this._shared.uColC.value.set(pick(2));
    // the gradient wants LIGHT colours: lift the lightness floor
    for (const u of [this._shared.uColA, this._shared.uColB, this._shared.uColC]) {
      u.value.getHSL(this._hsl);
      u.value.setHSL(this._hsl.h, Math.max(0.6, this._hsl.s), Math.max(0.55, this._hsl.l));
    }
    this._shared.uTint.value.copy(this._shared.uColA.value);
  }

  _loadArt(url) {
    const self = this;
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: false }, function (tex) {
      if (self._artUrl !== url || !self._scene) { tex.dispose(); return; }
      if (self._artTexture) self._artTexture.dispose();
      self._artTexture = tex;
      self._blurDirty = true;
      self._reframe();
    });
  }

  // --- the data: 48 bins from three bands -------------------------------------------
  _reconstruct(bands, dt) {
    const B = this._bins;
    const b = [bands.low, bands.mid, bands.high];
    for (let k = 0; k < B; k++) {
      const x = (k + 0.5) / B;
      let v = 0, wsum = 0;
      for (let i = 0; i < 3; i++) {
        const d = (x - BAND_CENTRES[i]) / BAND_SIGMA;
        const w = Math.exp(-d * d);
        v += w * b[i]; wsum += w;
      }
      v = wsum > 0 ? v / wsum : 0;
      v *= 1 - TILT * x;
      this._noisePhase[k] += dt * this._noiseRate[k];
      v *= 1 + DECORR * Math.sin(this._noisePhase[k]);
      // the kick spike on the bass end
      if (this._kick > 0 && x < KICK_BINS) v += KICK_SPIKE * this._kick * (1 - x / KICK_BINS);
      this._target[k] = clamp01(v);
    }
    this._kick = Math.max(0, this._kick - dt * 12);
  }

  // Instant attack, linear fall, a peak that hangs.
  _ballistics(dt) {
    const B = this._bins;
    for (let k = 0; k < B; k++) {
      const t = this._target[k];
      let v = this._value[k];
      if (t >= v) v = t; else v = Math.max(t, v - FALL_PER_S * dt);
      this._value[k] = v;
      if (v >= this._peak[k]) { this._peak[k] = v; this._peakHold[k] = PEAK_HOLD_S; }
      else if (this._peakHold[k] > 0) this._peakHold[k] -= dt;
      else this._peak[k] = Math.max(v, this._peak[k] - PEAK_FALL_PER_S * dt);
    }
  }

  // --- the ring, written per frame --------------------------------------------------
  _writeBars() {
    const B = this._bins, N = this._barCount;
    const R = this._radius;
    const mode = this._mode;
    const maxLen = BAR_MAX_LEN * 2;      // height units
    // the rotation tick: a notch that settles back, so the mirror holds
    const notch = this._notchT < NOTCH_S ? (1 - this._notchT / NOTCH_S) : 0;
    const notchA = notch * this._notchDir * (Math.PI * 2 / N);
    const double = mode === MODE_DOUBLE;
    const half = double ? B / 2 : B;       // bins per side per ring
    const spacing = Math.PI / half;
    for (let i = 0; i < N; i++) {
      let ring = 0, side, k, idx;
      if (double) { ring = i < B ? 0 : 1; const j = i % B; side = j < half ? 1 : -1; idx = j % half; k = ring * half + idx; }
      else { side = i < B ? 1 : -1; idx = i % B; k = idx; }
      // bass at the bottom, treble at the top; the left side mirrors the right
      let a = -Math.PI / 2 + (idx + 0.5) * spacing;
      if (side < 0) a = Math.PI - a;
      const radius = double ? R * (ring ? DOUBLE_OUTER_K : DOUBLE_INNER_K) : R;
      a += (ring ? -1 : 1) * notchA;
      const width = radius * spacing * BAR_WIDTH_K * (double ? 0.62 : 1);
      const len = this._value[k] * maxLen * (double ? DOUBLE_LEN_K : 1);
      const t = (a + Math.PI / 2) / (Math.PI * 2);
      this._aAngle.setX(i, a); this._aRadius.setX(i, radius); this._aLen.setX(i, len); this._aWidth.setX(i, width); this._aT.setX(i, t);
      // the peak cap
      const j = N + i;
      const peak = this._peak[k] * maxLen * (double ? DOUBLE_LEN_K : 1);
      this._aAngle.setX(j, a); this._aRadius.setX(j, radius + peak + PEAK_CAP_LEN * 0.6); this._aLen.setX(j, PEAK_CAP_LEN); this._aWidth.setX(j, width); this._aT.setX(j, t);
      // the contour through the bar tops, in angular order from the bottom
      const order = side > 0 ? idx : N / (double ? 2 : 1) - 1 - idx;
      if (!double || ring === 1) {
        const pt = this._curveIn[order];
        const rr = (radius + len) * this._shared.uScale.value;
        pt[0] = this._centre.x + Math.cos(a) * rr; pt[1] = this._centre.y + Math.sin(a) * rr;
      }
    }
    for (const a of [this._aAngle, this._aRadius, this._aLen, this._aWidth, this._aT]) a.needsUpdate = true;
    this._bars.visible = mode !== MODE_WAVE;
    this._barUniforms.uDots.value = mode === MODE_DOTS ? 1 : 0;
    this._writeCurve(double ? B : N, double ? R * DOUBLE_OUTER_K : R);

    if (this._rawView) this._writeRawRibbon();
  }

  _writeRawRibbon() {
    // The reconstructed bins as a step curve along the bottom edge, drawn
    // with the fill ribbon's material in its own geometry.
    const B = this._bins;
    if (!this._rawRibbon) {
      const g = new THREE.BufferGeometry();
      const pos = new THREE.BufferAttribute(new Float32Array(B * 4 * 3), 3);
      pos.setUsage(THREE.DynamicDrawUsage);
      const side = new Float32Array(B * 4), t = new Float32Array(B * 4);
      for (let k = 0; k < B; k++) { side[k * 4] = 0; side[k * 4 + 1] = 0; side[k * 4 + 2] = 0; side[k * 4 + 3] = 0; for (let j = 0; j < 4; j++) t[k * 4 + j] = k / B; }
      g.setAttribute('position', pos); g.setAttribute('aSide', new THREE.BufferAttribute(side, 1)); g.setAttribute('aT', new THREE.BufferAttribute(t, 1));
      const idx = [];
      for (let k = 0; k < B; k++) { const a = k * 4; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
      g.setIndex(idx);
      this._rawRibbon = new THREE.Mesh(g, this._curve.mat);
      this._rawRibbon.frustumCulled = false;
      this._rawRibbon.renderOrder = 4;
      this._scene.add(this._rawRibbon);
      this._rawPos = pos;
    }
    const w = 2 * this._aspect / B;
    for (let k = 0; k < B; k++) {
      const x0 = -this._aspect + k * w + w * 0.15, x1 = -this._aspect + (k + 1) * w - w * 0.15;
      const y0 = -0.98, y1 = -0.98 + this._value[k] * 0.5;
      const o = k * 4;
      this._rawPos.setXYZ(o, x0, y0, 0); this._rawPos.setXYZ(o + 1, x1, y0, 0);
      this._rawPos.setXYZ(o + 2, x0, y1, 0); this._rawPos.setXYZ(o + 3, x1, y1, 0);
    }
    this._rawPos.needsUpdate = true;
  }

  _writeCurve(n, baseR) {
    const pts = this._curveIn.slice(0, n);
    const count = catmullRom(pts, CURVE_SUBDIV, this._curvePts);
    const wave = this._mode === MODE_WAVE;
    const px = (wave ? WAVE_PX : CURVE_PX);
    // half width in height units: px / (H/2)
    const heightPx = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 1080;
    const hwu = px / (heightPx / 2);
    const pos = this._curve.pos, tt = this._curve.t;
    const fpos = this._fill.pos, ft = this._fill.t;
    const cx = this._centre.x, cy = this._centre.y;
    const scale = this._shared.uScale.value;
    for (let i = 0; i <= count; i++) {
      const j = i % count;
      const x = this._curvePts[j * 2], y = this._curvePts[j * 2 + 1];
      const dx = x - cx, dy = y - cy;
      const d = Math.sqrt(dx * dx + dy * dy) || 1;
      const nx = dx / d, ny = dy / d;
      pos.setXYZ(i * 2, x - nx * hwu, y - ny * hwu, 0);
      pos.setXYZ(i * 2 + 1, x + nx * hwu, y + ny * hwu, 0);
      const t = (Math.atan2(dy, dx) + Math.PI / 2) / (Math.PI * 2);
      tt.setX(i * 2, t); tt.setX(i * 2 + 1, t);
      if (wave) {
        const r0 = baseR * scale;
        fpos.setXYZ(i * 2, cx + nx * r0, cy + ny * r0, 0);
        fpos.setXYZ(i * 2 + 1, x, y, 0);
        ft.setX(i * 2, t); ft.setX(i * 2 + 1, t);
      }
    }
    pos.needsUpdate = true; tt.needsUpdate = true;
    this._curve.geo.setDrawRange(0, count * 6);
    this._fill.mesh.visible = wave;
    if (wave) { fpos.needsUpdate = true; ft.needsUpdate = true; this._fill.geo.setDrawRange(0, count * 6); }
  }

  // --- particles ----------------------------------------------------------------
  _emit(count, speedK) {
    const n = this._pCount;
    for (let c = 0; c < count; c++) {
      const i = this._pNext; this._pNext = (this._pNext + 1) % n;
      const a = Math.random() * Math.PI * 2;
      const r = this._radius * this._shared.uScale.value + rand(0, 0.05);
      const sp = rand(PARTICLE_SPEED[0], PARTICLE_SPEED[1]) * speedK;
      this._pX[i] = this._centre.x + Math.cos(a) * r; this._pY[i] = this._centre.y + Math.sin(a) * r;
      this._pVX[i] = Math.cos(a) * sp; this._pVY[i] = Math.sin(a) * sp;
      this._pMax[i] = rand(PARTICLE_LIFE[0], PARTICLE_LIFE[1]);
      this._pLife[i] = this._pMax[i];
    }
  }

  _stepParticles(dt) {
    const n = this._pCount;
    const pos = this._pPos, al = this._pAlpha;
    for (let i = 0; i < n; i++) {
      if (this._pLife[i] <= 0) { al.setX(i * 2, 0); al.setX(i * 2 + 1, 0); pos.setXYZ(i * 2, 0, -5, 0); pos.setXYZ(i * 2 + 1, 0, -5, 0); continue; }
      this._pLife[i] -= dt;
      const acc = 1 + PARTICLE_ACCEL * dt;
      this._pVX[i] *= acc; this._pVY[i] *= acc;
      this._pX[i] += this._pVX[i] * dt; this._pY[i] += this._pVY[i] * dt;
      const life = clamp01(this._pLife[i] / this._pMax[i]);
      const a = life * (1 - life) * 4;      // in and out
      pos.setXYZ(i * 2, this._pX[i], this._pY[i], 0);
      pos.setXYZ(i * 2 + 1, this._pX[i] - this._pVX[i] * TRAIL_S, this._pY[i] - this._pVY[i] * TRAIL_S, 0);
      al.setX(i * 2, a); al.setX(i * 2 + 1, 0);
    }
    pos.needsUpdate = true; al.needsUpdate = true;
  }

  _stepStars(dt) {
    const n = this._sCount;
    for (let i = 0; i < n; i++) {
      this._sR[i] += STAR_SPEED * dt * (0.4 + this._sR[i]);
      if (this._sR[i] > 2.2) { this._sR[i] = 0.15; this._sA[i] = Math.random() * Math.PI * 2; }
      this._sPos.setXYZ(i, this._centre.x + Math.cos(this._sA[i]) * this._sR[i], this._centre.y + Math.sin(this._sA[i]) * this._sR[i], 0);
    }
    this._sPos.needsUpdate = true;
  }

  // --- the rhythm --------------------------------------------------------------------
  _onBeat(strong) {
    this._step++;
    this._sinceStep = 0;
    this._kick = 1;
    this._shake(1);
    this._pulseT = 0;
    this._emit(strong ? BEAT_BURST[1] : BEAT_BURST[0], 0.6 + 0.8 * this._bands.low);
  }

  _shake(k) {
    this._shakeT = 0;
    this._shakeK = k;
    const a = Math.random() * Math.PI * 2;
    this._shakeDir.set(Math.cos(a), Math.sin(a));
  }

  _onBar() {
    this._notchT = 0;
    this._notchDir = Math.random() < 0.5 ? 1 : -1;
    this._gradShift = (this._gradShift + 1 / this._barCount) % 1;
  }

  _onPhrase() {
    if (this._forcedMode != null) return;
    this._mode = (this._mode + 1 + Math.floor(Math.random() * (MODE_COUNT - 1))) % MODE_COUNT;
  }

  _onDrop() {
    this._flashFrames = FLASH_FRAMES;
    this._emit(DROP_BURST, 1.6);
    this._dropT = 0;
    this._shake(DROP_SHAKE_K);
  }

  _stepSynthBands(deltaTime, profile) {
    const k = Math.exp(-SYNTH_DECAY * deltaTime);
    const sy = this._synth;
    sy.low = SYNTH_BASE + (sy.low - SYNTH_BASE) * k;
    sy.mid = SYNTH_BASE + (sy.mid - SYNTH_BASE) * k;
    sy.high = SYNTH_BASE + (sy.high - SYNTH_BASE) * k;
    const fired = (key, phase) => { const prev = this._synthPhase[key]; this._synthPhase[key] = phase; return prev != null && phase < prev; };
    const bar = profile.barPhase, beat = profile.beatPhase;
    if (bar != null && beat != null) {
      if (fired('low', beat)) sy.low = 1.0;
      if (fired('mid', (bar + 0.5) % 1)) sy.mid = 0.85;
      if (fired('high', (beat + 0.5) % 1)) sy.high = 0.75;
      return;
    }
    const t = this._time;
    if (fired('low', (t / SYNTH_PERIOD_LOW) % 1)) sy.low = 1.0;
    if (fired('mid', (t / SYNTH_PERIOD_MID) % 1)) sy.mid = 0.85;
    if (fired('high', (t / SYNTH_PERIOD_HIGH) % 1)) sy.high = 0.75;
  }

  _effectiveBands(director) {
    if (director && director.hasReliableGrid && director.bands) return director.bands;
    return this._synth;
  }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.05);
    const playing = profile.playbackState === 'PLAYING';
    const director = profile.director;

    if (profile.artworkUrl !== this._artUrl) {
      this._artUrl = profile.artworkUrl;
      if (profile.artworkUrl) this._loadArt(profile.artworkUrl);
      else { this._artTexture = null; this._bgUniforms.uHas.value = 0; }
    }
    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile.artworkPalette);
      this._bgUniforms.uFallback.value.copy(this._shared.uColA.value).multiplyScalar(0.25);
    }
    const aspect = safeZoneUniforms.uAspect.value || 0;
    const half = safeZoneUniforms.uSafeHalfSize.value, stageHalf = safeZoneUniforms.uStageHalfSize.value;
    if ((aspect && aspect !== this._aspect) || half.x !== this._lastHalfX || half.y !== this._lastHalfY
        || stageHalf.x !== this._lastStageX || stageHalf.y !== this._lastStageY) {
      this._lastHalfX = half.x; this._lastHalfY = half.y;
      this._lastStageX = stageHalf.x; this._lastStageY = stageHalf.y;
      this._reframe();
    }

    if (!playing) {
      // paused: the bars fall and nothing else moves
      for (let k = 0; k < this._bins; k++) this._target[k] = 0;
      this._ballistics(dt);
      this._writeBars();
      return;
    }

    this._time += dt;
    this._sinceStep += dt;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || SYNTH_BPM;
    this._beatPeriod = 60 / bpm;
    this._stepSynthBands(dt, profile);
    const bands = this._effectiveBands(director);
    this._bands.low = bands && bands.low != null ? bands.low : 0.5;
    this._bands.mid = bands && bands.mid != null ? bands.mid : 0.5;
    this._bands.high = bands && bands.high != null ? bands.high : 0.5;

    // --- beat, bar, phrase, drop
    const beatPhase = director && director.beat != null ? director.beat : profile.beatPhase;
    const accent = director && director.accent != null ? director.accent : null;
    if (beatPhase != null) {
      if (this._lastBeatPhase != null && beatPhase < this._lastBeatPhase) this._onBeat(accent != null ? accent > 0.5 : this._step % 4 === 0);
      this._lastBeatPhase = beatPhase;
    } else if (this._sinceStep >= this._beatPeriod) {
      this._onBeat(this._step % 4 === 0);
    }
    const barPhase = director && director.bar != null ? director.bar : profile.barPhase;
    if (barPhase != null) {
      if (this._lastBarPhase != null && barPhase < this._lastBarPhase) this._onBar();
      this._lastBarPhase = barPhase;
    } else if (this._sinceStep === 0 && this._step % 4 === 0) this._onBar();
    const phrasePhase = director && director.phrase != null ? director.phrase : profile.phrasePhase;
    if (phrasePhase != null) {
      if (this._lastPhrasePhase != null && phrasePhase < this._lastPhrasePhase) this._onPhrase();
      this._lastPhrasePhase = phrasePhase;
    } else if (this._sinceStep === 0 && this._step > 0 && this._step % 16 === 0) this._onPhrase();
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._lastMode !== 'drop') this._onDrop();
    this._lastMode = mode;

    // --- the data and the ring
    this._reconstruct(this._bands, dt);
    this._ballistics(dt);
    this._shakeT += dt; this._pulseT += dt; this._notchT += dt;
    let scale = 1 + (PULSE_SCALE - 1) * (this._pulseT < PULSE_S ? 1 - this._pulseT / PULSE_S : 0);
    if (this._dropT >= 0) {
      this._dropT += dt / this._beatPeriod;
      if (this._dropT < DROP_RING_BEATS) { const e = 1 - this._dropT / DROP_RING_BEATS; scale *= 1 + e * e; }
      else this._dropT = -1;
    }
    this._shared.uScale.value = scale;
    this._shared.uGradShift.value = this._gradShift;
    this._shared.uGain.value = 1;
    this._writeBars();

    // --- the background: the shake, the lows, the flash
    const sh = this._shakeT < SHAKE_S ? 1 - this._shakeT / SHAKE_S : 0;
    const heightPx = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 1080;
    const px = SHAKE_PX * this._shakeK * sh / heightPx;
    this._bgUniforms.uShake.value.set(this._shakeDir.x * px, this._shakeDir.y * px);
    this._bgUniforms.uZoom.value = 1 + (SHAKE_ZOOM - 1) * sh * Math.min(this._shakeK, 1.5);
    const wantBright = this._bands.low > 0.7 ? BG_BRIGHT : BG_DARKEN;
    this._bright += (wantBright - this._bright) * Math.min(1, dt / (wantBright > this._bright ? 0.04 : 0.25));
    this._bgUniforms.uDarken.value = this._bright;
    if (this._flashFrames > 0) { this._bgUniforms.uFlash.value = 1; this._flashFrames--; } else this._bgUniforms.uFlash.value = 0;

    this._stepParticles(dt);
    if (this._stars) this._stepStars(dt);
    if (this._probe && typeof window !== 'undefined') window.__pulsar = this.getProbe();
  }

  _curveBox() {
    const n = this._curveSamples;
    let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity;
    for (let i = 0; i < n; i++) { const x = this._curvePts[i * 2], y = this._curvePts[i * 2 + 1]; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    return [+x0.toFixed(3), +y0.toFixed(3), +x1.toFixed(3), +y1.toFixed(3)];
  }

  getProbe() {
    return {
      t: this._time, step: this._step, mode: this._mode,
      shake: this._shakeT < SHAKE_S ? 1 - this._shakeT / SHAKE_S : 0, shakeT: this._shakeT,
      pulse: this._shared.uScale.value, bright: this._bright, flash: this._bgUniforms.uFlash.value,
      centre: [this._centre.x, this._centre.y], radius: this._radius, coverAbsent: !!this._coverAbsent,
      target: Array.from(this._target), value: Array.from(this._value), peak: Array.from(this._peak),
      angles: Array.from(this._aAngle.array.slice(0, this._barCount)), lens: Array.from(this._aLen.array.slice(0, this._barCount)),
      widths: Array.from(this._aWidth.array.slice(0, 2)), aspect: this._shared.uAspect.value,
      curveBox: this._curveBox(),
      alive: Array.from(this._pLife).filter((l) => l > 0).length
    };
  }

  dispose() {
    const s = this._scene;
    s.remove(this._bg); this._bgGeo.dispose(); this._bgMat.dispose();
    this._copyMat.dispose(); this._gaussMat.dispose();
    for (const rt of this._pyramid) rt.dispose();
    this._blurRT.dispose();
    s.remove(this._bars); this._barGeo.dispose(); this._barMat.dispose();
    if (this._rawRibbon) { s.remove(this._rawRibbon); this._rawRibbon.geometry.dispose(); }
    for (const r of [this._curve, this._fill]) { s.remove(r.mesh); r.geo.dispose(); r.mat.dispose(); }
    s.remove(this._trails); this._trailGeo.dispose(); this._trailMat.dispose();
    if (this._stars) { s.remove(this._stars); this._starGeo.dispose(); this._starMat.dispose(); }
    if (this._artTexture) { this._artTexture.dispose(); this._artTexture = null; }
    this._scene = null;
    if (typeof window !== 'undefined' && window.__pulsar) delete window.__pulsar;
  }
}
